import { useAuthStore } from "@/stores/auth";
import type { ApiErrorBody, SessionResponse } from "@/types/api";

/** Same-origin by default, in development (via the Vite proxy) and in production. */
const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "/api";

/** A structured failure from the API. Carries the server's error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True when the server rejected specific fields, so a form can show them inline. */
  get hasFieldErrors(): boolean {
    return this.fields !== undefined && Object.keys(this.fields).length > 0;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Skip the refresh-and-retry dance. Set for the auth endpoints themselves,
   * where a 401 is the answer rather than a stale token.
   */
  skipRefresh?: boolean;
  /**
   * What the caller will accept. Defaults to JSON, which is every endpoint
   * but the report -- see `api.blob`.
   */
  accept?: string;
}

/**
 * The in-flight refresh, if any.
 *
 * Without this, a page that fires five queries at once would, on an expired
 * token, send five refresh requests. Since refresh tokens rotate and reuse is
 * treated as theft, four of them would be rejected and the server would revoke
 * every session. One shared promise is what makes rotation safe here.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return false;

    const session = (await response.json()) as SessionResponse;
    useAuthStore.getState().setSession(session);
    return true;
  } catch {
    // Network failure: indistinguishable from an expired session here, and
    // treated the same way by the caller.
    return false;
  }
}

function send(path: string, options: RequestOptions): Promise<Response> {
  const headers = new Headers({ Accept: options.accept ?? "application/json" });

  const { accessToken } = useAuthStore.getState();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  let body: string | undefined;
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }

  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    body,
    // Carries the HttpOnly refresh cookie. same-origin rather than include,
    // because the API is served from this very origin.
    credentials: "same-origin",
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "unexpected_error";
  let message = "Algo deu errado. Tente novamente.";
  let fields: Record<string, string> | undefined;

  try {
    const body = (await response.json()) as { error?: ApiErrorBody };
    if (body.error) {
      code = body.error.code || code;
      message = body.error.message || message;
      fields = body.error.fields;
    }
  } catch {
    // A non-JSON body (a proxy error page, say) leaves the defaults in place.
  }

  return new ApiError(response.status, code, message, fields);
}

async function parse<T>(response: Response): Promise<T> {
  if (!response.ok) throw await toApiError(response);
  // 204 and empty bodies: there is nothing to decode.
  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * One request, with the access token's expiry handled.
 *
 * On a 401 it refreshes once and replays, which is what keeps a 15-minute
 * access token invisible to the user. If the refresh fails the session is
 * cleared, and the route guards take the user to the sign-in page.
 *
 * It hands back the Response rather than a decoded body, so that this -- the
 * part that must not be reimplemented -- is shared by every kind of answer the
 * API gives. The report is a PDF; everything else is JSON.
 */
async function request(path: string, options: RequestOptions): Promise<Response> {
  const response = await send(path, options);

  if (response.status === 401 && !options.skipRefresh) {
    if (await refreshSession()) {
      return await send(path, { ...options, skipRefresh: true });
    }
    useAuthStore.getState().clear();
    throw await toApiError(response);
  }

  return response;
}

/** Call the API and decode its JSON. */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return parse<T>(await request(path, options));
}

/** A file the API generated, with the name it asked to be saved under. */
export interface DownloadedFile {
  blob: Blob;
  /** From Content-Disposition; null when the response did not name one. */
  fileName: string | null;
}

/**
 * Call the API for a file rather than for JSON.
 *
 * A download cannot be a plain link: the access token lives in memory and
 * travels in an Authorization header, so the browser's own navigation would
 * arrive unauthenticated. The bytes come back here and reach the disk through
 * `saveBlob` in lib/download.ts.
 *
 * A failure is still the JSON envelope -- the server renders its errors the
 * same way whatever the route would have answered with -- so it goes through
 * the same mapping as every other call and reaches the UI as an ApiError
 * carrying the server's own sentence.
 */
export async function apiDownload(
  path: string,
  options: RequestOptions = {},
): Promise<DownloadedFile> {
  const response = await request(path, options);
  if (!response.ok) throw await toApiError(response);

  return {
    blob: await response.blob(),
    fileName: fileNameFrom(response.headers.get("Content-Disposition")),
  };
}

/**
 * The file name out of a Content-Disposition header.
 *
 * Only the plain `filename` form: this API keeps its file names ASCII
 * precisely so there is one of them to read -- see reportFileName in
 * server/internal/ticket/report_handler.go. The path separators are stripped
 * because the name reaches `a.download`, and a server is not the place a
 * client takes directory instructions from.
 */
function fileNameFrom(header: string | null): string | null {
  if (!header) return null;

  const match = /filename="([^"]+)"|filename=([^;]+)/i.exec(header);
  const raw = (match?.[1] ?? match?.[2] ?? "").trim();
  if (!raw) return null;

  const name = raw.replace(/[/\\]/g, "");
  return name || null;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiFetch<T>(path, { ...options }),
  /** A generated file, with the name the server gave it. */
  blob: (path: string, accept: string, options?: RequestOptions) =>
    apiDownload(path, { ...options, accept }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "PATCH", body }),
  del: <T = void>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "DELETE" }),
};

/**
 * Try to restore a session from the HttpOnly refresh cookie.
 *
 * Called once at boot: it is what makes a hard refresh keep the user signed
 * in without the access token ever being stored where a script can read it.
 */
export function restoreSession(): Promise<boolean> {
  return refreshSession();
}

/** Exported for tests, which need a clean slate between cases. */
export function __resetRefreshState(): void {
  refreshInFlight = null;
}
